import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Dashboard from '../pages/Dashboard';
import Home from '../pages/Home';
import { authService } from '../services/AuthService';

/**
 * Component that conditionally renders Dashboard or Home based on authentication status
 * Environment-aware: Ensures authentication state is isolated between dev/prod
 * 
 * FIXED: Complete hydration before navigation decisions
 */
export default function RootRoute() {
  const [accessState, setAccessState] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthAndAccessState = async () => {
      try {
        setIsLoading(true);

        // Step 1: Validate authentication with backend
        const isValid = await authService.validateAuthentication();
        setIsAuthenticated(isValid);

        if (!isValid) {
          setIsLoading(false);
          return;
        }

        // Step 2: Get complete access state using AuthService (avoid cache conflicts)
        const accessCheck = await authService.checkAccessState();

        if (!accessCheck.isAuthenticated) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Create state object for compatibility
        const state = {
          emailVerified: accessCheck.emailVerified,
          shouldGoToDashboard: accessCheck.hasFullAccess,
          hasActiveSubscription: accessCheck.hasPayment
        };
        setAccessState(state);

        // Step 3: Navigation decision based on complete state
        if (!state.emailVerified) {
          setIsLoading(false);
          return;
        }

        if (state.shouldGoToDashboard) {
          setIsLoading(false);
          return; // Stay on root to show Dashboard
        }

        // Redirect to subscription offers
        setIsRedirecting(true);
        navigate('/subscription-offers', { replace: true });

      } catch (error) {
        console.error('❌ RootRoute: Error in hydration:', error);
        setIsAuthenticated(false);
        setAccessState(null);
        setIsLoading(false);
      }
    };

    checkAuthAndAccessState();
  }, [navigate]);

  // Loading state during hydration
  if (isLoading || isRedirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isRedirecting ? 'Redirecionando...' : 'Verificando acesso...'}
          </p>
        </div>
      </div>
    );
  }

  // Show Dashboard if user is authenticated and has payment
  if (isAuthenticated && accessState?.shouldGoToDashboard) {
    return <Dashboard />;
  }

  // Show Home page for all other cases
  return <Home />;
}